import type { GetWorkspacePageByIdQuery, PublicDocMode } from '@affine/graphql';
import {
  catchErrorInto,
  effect,
  Entity,
  fromPromise,
  LiveData,
  mapInto,
  onComplete,
  onStart,
} from '@toeverything/infra';
import { switchMap } from 'rxjs';

import type { DocService, DocsService } from '../../doc';
import type { WorkspaceService } from '../../workspace';
import type { ShareStore } from '../stores/share';

type ShareInfoType = GetWorkspacePageByIdQuery['workspace']['doc'];

export class ShareInfo extends Entity {
  info$ = new LiveData<ShareInfoType | undefined | null>(null);
  isShared$ = this.info$.map(info => info?.public);
  sharedMode$ = this.info$.map(info => (info !== null ? info?.mode : null));

  error$ = new LiveData<any>(null);
  isRevalidating$ = new LiveData<boolean>(false);
  isMutating$ = new LiveData<boolean>(false);

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly docService: DocService,
    private readonly store: ShareStore,
    private readonly docsService: DocsService
  ) {
    super();
  }

  revalidate = effect(
    switchMap(() => {
      return fromPromise(signal =>
        this.store.getShareInfoByDocId(
          this.workspaceService.workspace.id,
          this.docService.doc.id,
          signal
        )
      ).pipe(
        mapInto(this.info$),
        catchErrorInto(this.error$),
        onStart(() => {
          this.error$.next(null);
          this.isRevalidating$.next(true);
        }),
        onComplete(() => this.isRevalidating$.next(false))
      );
    })
  );

  async waitForRevalidation(signal?: AbortSignal) {
    this.revalidate();
    await this.isRevalidating$.waitFor(v => v === false, signal);
    if (this.error$.value) throw this.error$.value;
    if (!this.info$.value) throw new Error('Sharing state unavailable');
  }

  async enableShare(mode: PublicDocMode) {
    if (this.isMutating$.value) return;
    this.isMutating$.next(true);
    try {
      const confirmed = await this.docsService.waitForServerConfirmed(
        this.docService.doc.id,
        { timeoutMs: 30_000, pollMs: 250 }
      );
      if (!confirmed) {
        throw new Error('Document server confirmation pending');
      }
      await this.store.enableSharePage(
        this.workspaceService.workspace.id,
        this.docService.doc.id,
        mode
      );
      await this.waitForRevalidation();
    } finally {
      this.isMutating$.next(false);
    }
  }

  async changeShare(mode: PublicDocMode) {
    await this.enableShare(mode);
  }

  async disableShare() {
    if (this.isMutating$.value) return;
    this.isMutating$.next(true);
    try {
      await this.store.disableSharePage(
        this.workspaceService.workspace.id,
        this.docService.doc.id
      );
      await this.waitForRevalidation();
    } finally {
      this.isMutating$.next(false);
    }
  }
}
